import React, { useState } from "react";
import { FilePenLine, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { DOC_CHIP, docApi, docPref, isPmc, isSa } from "@/lib/docApi";
import { Empty, ErrorBox, Loading, StatusFilter, SummaryChip, btnGold, useLoad, when } from "@/components/nds/windows/finance/parts";
import { StatusChip } from "@/components/nds/windows/doc/parts";
import GenerateForm from "@/components/nds/windows/doc/GenerateForm";
import DocumentDetail from "@/components/nds/windows/doc/DocumentDetail";

export default function DocGeneratorContent() {
  const { user } = useAuth();
  const [status, setStatusState] = useState(() => docPref.get("gen_status", "all"));
  const setStatus = (v) => { docPref.set("gen_status", v); setStatusState(v); };
  const [view, setView] = useState(null);
  const [state, load] = useLoad(() => Promise.all([docApi.get("/documents"), docApi.get("/templates")]).then(([d, t]) => ({ ...d, ...t })), []);
  if (state.loading && !state.data) return <Loading testid="doc-gen-loading" />;
  if (state.error) return <ErrorBox testid="doc-gen-error" error={state.error} onRetry={load} />;
  const { documents, templates } = state.data;
  if (view === "new") return <GenerateForm templates={templates} onCancel={() => setView(null)} onCreated={(d) => { load(); setView(d.id); }} />;
  const rec = view && documents.find((d) => d.id === view);
  if (rec) return <DocumentDetail key={`${rec.id}-${rec.updated_at}`} rec={rec} pmc={isPmc(user)} sa={isSa(user)} onBack={() => setView(null)} onChanged={load} />;
  const counts = documents.reduce((a, d) => ({ ...a, [d.status]: (a[d.status] || 0) + 1 }), { all: documents.length });
  const list = documents.filter((d) => status === "all" || d.status === status);
  return (
    <div data-testid="doc-gen" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
        <FilePenLine className="h-4 w-4 text-nex-gold" />
        <SummaryChip tone="yellow" testid="doc-gen-summary">{(counts.draft || 0) + (counts.review || 0)} in draft / review</SummaryChip>
        <StatusFilter prefix="doc-gen" chips={DOC_CHIP} value={status} onChange={setStatus} counts={counts} />
        <button type="button" onClick={() => setView("new")} data-testid="doc-gen-new" className={`${btnGold} ml-auto inline-flex items-center gap-1`}><Plus className="h-3 w-3" /> New document</button>
      </div>
      <ul data-testid="doc-gen-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
        {list.map((d) => (
          <li key={d.id}>
            <button type="button" onClick={() => setView(d.id)} data-testid={`doc-gen-row-${d.id}`} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-nex-gold/5">
              <span className="flex-1 min-w-0 truncate text-nex-silver font-medium">{d.title}</span>
              <span className="text-xs text-nex-muted hidden sm:inline">{d.document_type}</span>
              <span className="font-mono text-[10px] text-nex-muted">{when(d.created_at)}</span>
              <StatusChip map={DOC_CHIP} status={d.status} />
            </button>
          </li>
        ))}
        {!list.length && <li><Empty testid="doc-gen-empty">No documents yet. Click New document to generate one from a template.</Empty></li>}
      </ul>
    </div>
  );
}
